import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState
} from 'react';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';
import {
  Checkbox,
  Container,
  FormControlLabel,
  Grid,
  Typography,
  makeStyles
} from '@material-ui/core';
import CheckBoxOutlineBlankIcon from '@material-ui/icons/CheckBoxOutlineBlank';
import CheckBoxIcon from '@material-ui/icons/CheckBox';

import InvoiceHelpDialog from '../../../Dialogs/InvoiceHelpDialog/InvoiceHelpDialog';
import { HELP_TEXT } from '../../../../Utils/enums';
import SimpleDivider from '../../../Dividers/SimpleDivider';
import TextInput from '../../../Inputs/TextInput';
import QuestionMark from '../../../Adornments/QuestionMark';
import Recaptcha from '../../../Captcha/TurnstileManagedCaptcha';
import BaseButton from '../../../Buttons/BaseButton';
import { GetSingleContractAPI } from '../../../../API/Contracts/ContractsAPI';
import { extractErrorMessage } from '../../../../Utils/Errors/Errors';
import { AlertsDispatchContext } from '../../../../Contexts/AlertsContext';
import { Company } from '../../../../Configs/general';
import ContractCard from '../../../Cards/ContractCard';
import Title from '../../../Labels/Title';
import FooterStepper from '../../../Stepper/FooterStepper';
import { InvoiceHelpType } from '../../../../Enums/invoices';
import { useCaptcha } from '../../../../Hooks/useCaptcha';
import SweetAlert from '../../../Alerts/SweetAlert';
import { yupResolver } from '@hookform/resolvers/yup';
import { ROUTE_NAMES } from '../../../../Routes/Routes';
import { PrivacyPoliciesLink } from '../../../../Configs/Links';
import {
  TERMS,
  TERMS_ROUTES
} from '../../../../Views/Information/Information/Information';

const schema = yup.object({
  id: yup
    .number()
    .required(
      `Ingrese el número de ${Company.contractConjugation.regular.singular.main}`
    )
    .typeError(
      `El número de ${Company.contractConjugation.regular.singular.main} debe ser válido`
    )
    .transform((value, originalValue) => {
      return originalValue === '' ? undefined : value;
    }),
  terms: yup.boolean().oneOf([true], 'Debe aceptar los términos y condiciones')
});

const QueryContractForm = ({
  selectedContractInfo,
  setSelectedContractInfo,
  setValidatedContract,
  targetForm = '',
  queryTitle = 'Consultar contrato',
  queryReason = '',
  defaultValue = null
}) => {
  const {
    register,
    setValue,
    control,
    watch,
    trigger,
    errors,
    formState: { isValid },
    handleSubmit
  } = useForm({
    resolver: yupResolver(schema),
    mode: 'onBlur',
    defaultValues: { id: defaultValue, terms: false }
  });

  const classes = useStyles();
  const [loading, setLoading] = useState(false);
  const [helpDialogOpen, setHelpDialogOpen] = useState(false);
  const terms = watch('terms', false);
  const contractId = watch('id', false);

  const [isContract, setIsContract] = useState(false);
  const {
    captchaToken: captcha,
    isValidCaptcha,
    captchaRef: recaptchaRef,
    handlers: captchaHandlers
  } = useCaptcha();

  const setAlert = useContext(AlertsDispatchContext);

  const inputRef = useRef(null);
  const authToken = null;

  useEffect(() => {
    if (selectedContractInfo) {
      setValue('id', selectedContractInfo.id);
    }
  }, [setValue, selectedContractInfo]);

  const onClickHandler = useCallback(
    async data => {
      const captchaValue = captcha;
      captchaHandlers.reset();

      setLoading(true);

      const response = await GetSingleContractAPI(
        data.id,
        authToken,
        captchaValue
      );
      if (response.success) {
        const contractInfo = response.data.data;
        contractInfo.id = data.id;

        setSelectedContractInfo(contractInfo);
        setIsContract(true);
      } else {
        setValue('id', defaultValue || '');
        setIsContract(false);

        const errorMessage = extractErrorMessage(response).message;
        setAlert({
          type: 'error',
          message: errorMessage
        });
      }
      setLoading(false);
    },
    [
      setLoading,
      captcha,
      setAlert,
      setValue,
      setSelectedContractInfo,
      captchaHandlers,
      defaultValue
    ]
  );

  const goNext = useCallback(() => {
    setValidatedContract(true);
  }, [setValidatedContract]);

  const onHelpClick = useCallback(() => {
    setHelpDialogOpen(true);
  }, []);

  return (
    <>
      <Container maxWidth="md">
        <div className={classes.container}>
          <Title title={queryTitle} />
          <form id={targetForm} onSubmit={handleSubmit(goNext)}>
            <Typography className={classes.text}>
              Ingresa el número de{' '}
              {Company.contractConjugation.regular.singular.main} del cual
              deseas gestionar su {queryReason}
            </Typography>

            <Grid container className={classes.inputContainer}>
              <Grid container className={classes.inputContainer}>
                <InvoiceHelpDialog
                  open={helpDialogOpen}
                  type={InvoiceHelpType.Contract}
                  onClose={() => setHelpDialogOpen(false)}
                  title={HELP_TEXT.contract}
                />
                <Grid
                  item
                  sm={5}
                  xs={12}
                  className={classes.inputContainerItem}
                >
                  <TextInput
                    label={`Número de ${Company.contractConjugation.regular.singular.main}`}
                    autoFocus
                    inputRef={register}
                    control={control}
                    fullWidth
                    disabled={loading}
                    variant="outlined"
                    name="id"
                    error={Boolean(errors.id)}
                    InputProps={{
                      endAdornment: <QuestionMark onClick={onHelpClick} />,
                      inputProps: {
                        type: 'number',
                        inputMode: 'numeric'
                      }
                    }}
                  />
                </Grid>
                <Grid
                  item
                  sm={4}
                  xs={12}
                  ref={inputRef}
                  className={classes.recaptchaContainer}
                >
                  <Recaptcha
                    captchaRef={recaptchaRef}
                    referenceEl={inputRef}
                    heightScale={0.9}
                    locale="es"
                    onResolved={captchaHandlers.onResolved}
                    onExpired={captchaHandlers.onExpired}
                    onLoaded={captchaHandlers.onLoaded}
                  />
                </Grid>
                <Grid item sm={3} xs={12} className={classes.buttonContainer}>
                  <BaseButton
                    type="submit"
                    color="primary"
                    variant="outlined"
                    className={classes.button}
                    onClick={handleSubmit(onClickHandler)}
                    disabled={
                      !isValidCaptcha ||
                      !isValid ||
                      loading ||
                      !terms ||
                      !contractId
                    }
                    fullWidth
                  >
                    Consultar
                  </BaseButton>
                </Grid>
              </Grid>
              <Grid item xs={12} sm={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      id="Signup_input_terms"
                      inputRef={register}
                      name="terms"
                      onChange={() => trigger('terms')}
                      icon={
                        <CheckBoxOutlineBlankIcon
                          fontSize="small"
                          color="primary"
                        />
                      }
                      checkedIcon={
                        <CheckBoxIcon fontSize="small" color="primary" />
                      }
                    />
                  }
                  label={
                    <Typography className={classes.termsText}>
                      He leído y acepto los{' '}
                      <a
                        className={classes.link}
                        href={`${ROUTE_NAMES.information}/${
                          TERMS_ROUTES[TERMS.introduction]
                        }`}
                        rel="noopener noreferrer"
                        target="_blank"
                      >
                        términos y condiciones
                      </a>{' '}
                      de uso, la{' '}
                      <a
                        href={PrivacyPoliciesLink}
                        rel="noopener noreferrer"
                        target="_blank"
                      >
                        política y aviso de privacidad
                      </a>{' '}
                      y autorizo el tratamiento de mis datos personales.
                    </Typography>
                  }
                />
              </Grid>
            </Grid>
            <SimpleDivider />
            {isContract && (
              <>
                <SweetAlert
                  message={
                    'Valide la información mostrada. Si es correcto, presione “Siguiente”'
                  }
                />
                <Grid item xs={12} className={classes.contractContainer}>
                  <ContractCard
                    data={selectedContractInfo}
                    className={classes.contract}
                    loading={loading}
                    showName={false}
                    showStratum={false}
                  />
                </Grid>
              </>
            )}
          </form>
        </div>
      </Container>
      {targetForm && (
        <FooterStepper
          targetForm={targetForm}
          disabledNext={!isContract || !terms}
        />
      )}
      <InvoiceHelpDialog
        open={helpDialogOpen}
        type={InvoiceHelpType.Contract}
        onClose={() => setHelpDialogOpen(false)}
        title={HELP_TEXT.contract}
      />
    </>
  );
};

const useStyles = makeStyles(theme => ({
  container: {
    marginTop: theme.spacing(7),
    marginBottom: theme.spacing(10),
    height: '100%'
  },
  title: {
    fontSize: 18,
    fontWeight: 700,
    [theme.breakpoints.down('sm')]: {
      fontSize: 14
    }
  },
  text: {
    fontSize: 14
  },
  inputContainer: {
    marginTop: theme.spacing()
  },
  inputContainerItem: {
    [theme.breakpoints.up('sm')]: {
      paddingRight: theme.spacing(2)
    }
  },
  optionItemContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start'
  },
  optionItemAlias: {
    color: '#B8B8B8',
    fontSize: 12
  },
  buttonContainer: {
    marginTop: theme.spacing(2),
    [theme.breakpoints.up('sm')]: {
      paddingLeft: theme.spacing(2),
      paddingTop: 15,
      marginTop: 0
    }
  },
  button: {
    minHeight: 58
  },
  recaptchaContainer: {
    padding: 0,
    overflow: 'hidden'
  },
  contractCard: {
    width: '100%',
    marginBottom: theme.spacing(4)
  },
  emptyState: {
    marginTop: 0,
    marginBottom: theme.spacing(2)
  },
  contractContainer: {
    marginTop: theme.spacing(3),
    [theme.breakpoints.up('sm')]: {
      marginTop: theme.spacing(2)
    }
  },
  itemText: {
    fontSize: 14,
    fontWeight: 500,
    paddingLeft: theme.spacing(),
    paddingRight: theme.spacing(4),
    paddingBottom: theme.spacing(2),
    [theme.breakpoints.up(theme.breakpoints.values.sm)]: {
      paddingBottom: 0
    }
  },
  itemContainer: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingTop: theme.spacing(5),
    paddingBottom: theme.spacing(5),
    [theme.breakpoints.up(theme.breakpoints.values.sm)]: {
      flexDirection: 'row'
    }
  },
  itemDivider: {
    marginTop: 0,
    marginBottom: 0
  }
}));

export default QueryContractForm;
